import { AxiosInstance } from "./AxiosInstance";

/**
 * Customer-facing messages for login and ordering. The wording is NOT kept in
 * the UI: the backend owns it (jaaga/app/shared/messages/customer.js) and serves
 * the whole list from GET /jaaga/api/public/messages. It is loaded once when the
 * app starts and kept in localStorage, so it is still there on the next visit
 * even if that load fails.
 *
 *   msg("LOGIN.CAPTCHA_FAILED")
 *   msg("LOGIN.OTP_SENT", { phone: "+91 98765 43210" })
 *   apiMsg(res?.payload?.payload?.status, "LOGIN.SEND_FAILED")
 */

const CACHE_KEY = "jw_messages";

// The only line written here: shown when the list has never loaded at all
// (first visit with the API unreachable).
const OFFLINE_LINE = "Something went wrong. Please try again.";

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || null;
  } catch {
    return null;
  }
};

let catalog = readCache();

/** Fetch the latest list from the backend; keeps the cached one on failure. */
export async function loadMessages() {
  try {
    const res = await AxiosInstance.get("/public/messages");
    const item = res?.data?.data?.item;
    if (item && typeof item === "object") {
      catalog = item;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(item));
      } catch {
        // storage full / blocked: the in-memory copy still works this visit
      }
    }
  } catch {
    // offline or API down: keep whatever was cached
  }
}

const fill = (text, vars = {}) =>
  text.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));

/**
 * A message by its key, e.g. "ORDER.FAILED", with `{name}` placeholders filled.
 * @param {string} key
 * @param {Record<string, string|number>} [vars]
 */
export function msg(key, vars) {
  const text = key.split(".").reduce((o, k) => o?.[k], catalog);
  if (typeof text === "string") return fill(text, vars);
  // Unknown key or list not loaded: the generic line from the list, if any.
  return catalog?.COMMON?.GENERIC || OFFLINE_LINE;
}

/**
 * The backend's own line from an API `status` object, else the message for
 * `fallbackKey` (used when there was no response, e.g. network down).
 * @param {{message?: string, description?: string}|undefined} status
 * @param {string} fallbackKey
 */
export function apiMsg(status, fallbackKey, vars) {
  // `message` first: on most endpoints `description` holds technical detail.
  return status?.message || status?.description || msg(fallbackKey, vars);
}
